/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import {
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom"; // ← tenant-aware nav
import Header, { HeaderType } from "../../components/Header";
import { GetProfile, UpdateProfile } from "../../services/api/profile-services";
import type { ProfileRequest } from "../../services/model/profileResponse";
import { getAccessToken, useAuth } from "../../context/auth";
import { useConfirm } from "@/components/dialog/confirm";
import BrandLoader from "@/branding/BrandLoader";

interface ProfileProps {
  style?: React.CSSProperties;
  loading: boolean;
}

type FormValues = {
  id: string;
  name: string;
  lastName: string;
  phone: string;
  email: string;
  policyNumber: string;
  idNumber: string;
};

const emptyValues = (): FormValues => ({
  id: "",
  name: "",
  lastName: "",
  phone: "",
  email: "",
  policyNumber: "",
  idNumber: "",
});

const numberRegex: RegExp = /^(\+\d{1,4}[- ]?)?\d{7,}$/;
const policyRegex: RegExp = /^[a-zA-Z0-9\-(@!\/#$%^&*()_)]{1,}$/;
const emailRegex: RegExp =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  lastName: Yup.string().required("Last Name is required"),
  email: Yup.string()
    .matches(emailRegex, "Email address is not valid")
    .required("Email address is required"),
  phone: Yup.string()
    .transform((v) => (v ? v.replace(/\s|-/g, "") : v))
    .matches(numberRegex, "Mobile number is not valid")
    .min(10, "Phone number is invalid")
    .required("Phone number is required"),
  policyNumber: Yup.string()
    .matches(policyRegex, "Policy Number is not valid")
    .required("Policy Number is required"),
});

const Profile: React.FC<ProfileProps> = () => {
  const [canClose, setCanClose] = useState(false);
  const [initialValues, setInitialValues] = useState<FormValues>(emptyValues());
  const [loading, setLoading] = useState(false);

  const { authTokens } = useAuth();
  const token = getAccessToken(authTokens) ?? "";
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>(); // ← tenant
  const tpath = (p: string) => (tenant ? `/${tenant}/${p}` : `/${p}`); // ← helper
  const confirm = useConfirm();

  const showDialog = (title: string, message: string) => {
    confirm({
      catchOnCancel: false,
      title,
      description: message,
    }).then(() => {
      if (canClose) navigate(tpath("dashboard"), { replace: true }); // ← tenant-aware
    });
  };

  const mapApiToForm = (api: any): FormValues => {
    const id = api?.id ?? "";
    const name = api?.firstName ?? api?.name ?? "";
    const lastName = api?.surname ?? api?.lastName ?? "";
    const phone = api?.phoneNumber ?? api?.phone ?? "";
    const email = api?.email ?? api?.email ?? "";
    const policyNumber = api?.policyNumber ?? "";
    const idNumber = api?.idNumber ?? api?.idNumber ?? "";
    return {
      id: String(id ?? ""),
      name: String(name ?? ""),
      lastName: String(lastName ?? ""),
      phone: String(phone ?? ""),
      email: String(email ?? ""),
      policyNumber: String(policyNumber ?? ""),
      idNumber: String(idNumber ?? ""),
    };
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const res = await GetProfile(token);
        const data = res?.data ?? {};
        setInitialValues(mapApiToForm(data));
        setLoading(false);
      } catch (err) {
        setLoading(false);
        showDialog(
          "Failure",
          "An exception occurred, please contact your administrator."
        );
        if (err instanceof Error) console.error(err.message);
      }
    };
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <Formik<FormValues>
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          try {
            setLoading(true);
            const payload: ProfileRequest = {
              id: values.id,
              firstName: values.name,
              surname: values.lastName,
              phoneNumber: values.phone,
              email: values.email,
              policyNumber: values.policyNumber,
            };
            const response = await UpdateProfile(token, payload);
            setLoading(false);
            setCanClose(true);
            showDialog(
              "Profile",
              response
                ? response.message
                : "There seems to be a problem with your internet connection. Please check your connection and try again."
            );
          } catch (err) {
            setLoading(false);
            showDialog(
              "Failure",
              "An exception occurred, please contact your administrator."
            );
            if (err instanceof Error) console.error(err.message);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <>
            <Header type={HeaderType.Profile} />
            <Container maxWidth="sm">
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Card variant="outlined">
                    <CardContent>
                      {/* Note text */}
                      <Grid
                        container
                        justifyContent="center"
                        alignItems="center"
                        direction="column"
                        sx={{ mt: 1, mb: 2 }}
                      >
                        <Typography align="center" variant="subtitle1">
                          Please note that your email address is your username.
                          Changing your email will change your username!
                        </Typography>
                      </Grid>

                      <Form noValidate>
                        <Grid container spacing={2}>
                          <Grid size={12}>
                            <TextField
                              type="text"
                              label="Email Address"
                              name="email"
                              placeholder="Eg. johndoe@anonymous.com"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              variant="outlined"
                              required
                              error={!!(errors.email && touched.email)}
                              helperText={touched.email && errors.email}
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              label="Name"
                              name="name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              required
                              variant="outlined"
                              error={Boolean(touched.name && errors.name)}
                              helperText={touched.name && errors.name}
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              label="Last Name"
                              name="lastName"
                              value={values.lastName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              required
                              variant="outlined"
                              error={Boolean(
                                touched.lastName && errors.lastName
                              )}
                              helperText={touched.lastName && errors.lastName}
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              label="Mobile Number"
                              name="phone"
                              placeholder="Eg. +2712 3456789; +27 123456789; 0123456789"
                              value={values.phone}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              required
                              variant="outlined"
                              error={Boolean(touched.phone && errors.phone)}
                              helperText={touched.phone && errors.phone}
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              label="Policy Number"
                              name="policyNumber"
                              value={values.policyNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              required
                              variant="outlined"
                              disabled
                              error={Boolean(
                                touched.policyNumber && errors.policyNumber
                              )}
                              helperText={
                                touched.policyNumber && errors.policyNumber
                              }
                            />
                          </Grid>

                          <Grid size={12}>
                            <TextField
                              label="ID Number"
                              name="idNumber"
                              value={
                                values.idNumber
                                  ? values.idNumber.replace(/.(?=.{4})/g, "*")
                                  : ""
                              }
                              fullWidth
                              variant="outlined"
                              disabled
                            />
                          </Grid>

                          <Grid size={12}>
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              fullWidth
                              size="large"
                              onClick={() => setCanClose(true)}
                            >
                              <strong>Save</strong>
                            </Button>
                          </Grid>
                        </Grid>
                      </Form>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Container>
          </>
        )}
      </Formik>

      <BrandLoader open={loading} />
    </div>
  );
};

export default Profile;
